import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Search } from "lucide-react";

/**
 * Tunisian postal address picker.
 * Lets the user search by code postal, delegation, locality or gouvernorat,
 * and fills 5 fields: code_postal, localite, delegation, gouvernorat + a "rue/quartier" free input.
 *
 * value/onChange follow a single object: { rue, code_postal, localite, delegation, gouvernorat, full }
 */
export default function AddressAutocomplete({ value, onChange, dataTestid = "address-search" }) {
  const v = value || { rue: "", code_postal: "", localite: "", delegation: "", gouvernorat: "", full: "" };
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenList(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const runSearch = async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get("/postal/search", { params: { q } });
      setResults(data);
      setOpenList(true);
    } catch (err) {
      console.warn("Postal search failed:", err);
    }
  };

  const pick = (r) => {
    const next = {
      rue: v.rue || "",
      code_postal: r.code_postal,
      localite: r.localite || "",
      delegation: r.delegation,
      gouvernorat: r.gouvernorat,
    };
    next.full = [next.rue, next.localite, `${next.code_postal} ${next.delegation}`, next.gouvernorat]
      .filter(Boolean)
      .join(" · ");
    onChange?.(next);
    setOpenList(false);
    setQuery("");
  };

  const updateRue = (rue) => {
    const next = { ...v, rue };
    next.full = [next.rue, next.localite, `${next.code_postal} ${next.delegation}`.trim(), next.gouvernorat]
      .filter((s) => s && s !== " ")
      .join(" · ");
    onChange?.(next);
  };

  return (
    <div className="space-y-2" ref={wrapRef}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid={dataTestid}
          type="search"
          name="address-autocomplete-search"
          autoComplete="off"
          value={query}
          onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
          onFocus={() => { if (results.length > 0) setOpenList(true); }}
          placeholder="Rechercher…"
          className="pl-9 rounded-sm"
        />
        {openList && results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-border rounded-sm shadow-md" data-testid={`${dataTestid}-list`}>
            {results.map((r, i) => (
              <button
                type="button"
                key={`${r.code_postal}-${r.delegation}-${r.localite || ""}-${i}`}
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 border-b last:border-b-0"
              >
                <span className="font-mono font-bold text-klein text-sm w-14 flex-shrink-0">{r.code_postal}</span>
                <span className="text-sm flex-1 min-w-0 truncate">
                  <span className="font-semibold">{r.localite || r.delegation}</span>
                  {r.localite && r.localite !== r.delegation && (
                    <span className="text-muted-foreground"> · {r.delegation}</span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground flex-shrink-0">{r.gouvernorat}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-3">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Rue / Quartier</Label>
          <Input
            data-testid={`${dataTestid}-rue`}
            value={v.rue}
            onChange={(e) => updateRue(e.target.value)}
            placeholder="Ex: 12 rue Habib Bourguiba"
            className="rounded-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Code postal</Label>
          <Input readOnly value={v.code_postal} className="rounded-sm mt-1 bg-slate-50 font-mono" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Localité</Label>
          <Input readOnly value={v.localite || ""} className="rounded-sm mt-1 bg-slate-50" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Délégation</Label>
          <Input readOnly value={v.delegation} className="rounded-sm mt-1 bg-slate-50" />
        </div>
        <div className="col-span-3">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Gouvernorat</Label>
          <Input readOnly value={v.gouvernorat} className="rounded-sm mt-1 bg-slate-50" />
        </div>
      </div>
      {v.full && (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <MapPin size={12} /> {v.full}
        </div>
      )}
    </div>
  );
}
